import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  Mail, Plus, Edit, Trash2, Send, Clock, CheckCircle2,
  Users, Loader2
} from 'lucide-react';
import EmailBuilder from './email-builder/EmailBuilder';
import { blocksToHtml } from './email-builder/emailBuilderUtils';

const NewsletterTab = ({ newsletters, customers, onRefresh, branding }) => {
  const { API, getAuthHeaders } = useAuth();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingNl, setEditingNl] = useState(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: '', subject: '', sender_name: '', reply_to: '',
    html_content: '', blocks: [], recipient_filter: 'all'
  });

  const openEditor = (nl = null) => {
    if (nl) {
      setEditingNl(nl);
      setForm({
        name: nl.name, subject: nl.subject, sender_name: nl.sender_name || '',
        reply_to: nl.reply_to || '', html_content: nl.html_content || '',
        blocks: nl.blocks || [], recipient_filter: nl.recipient_filter || 'all'
      });
    } else {
      setEditingNl(null);
      setForm({ name: '', subject: '', sender_name: '', reply_to: '', html_content: '', blocks: [], recipient_filter: 'all' });
    }
    setIsEditorOpen(true);
  };

  const handleBuilderChange = (newBlocks, html) => {
    setForm(prev => ({ ...prev, blocks: newBlocks, html_content: html }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        html_content: form.blocks.length > 0 ? blocksToHtml(form.blocks) : form.html_content
      };
      if (editingNl) {
        await axios.put(`${API}/marketing/newsletters/${editingNl.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${API}/marketing/newsletters`, payload, getAuthHeaders());
      }
      onRefresh();
      setIsEditorOpen(false);
    } catch (error) {
      console.error('Errore salvataggio newsletter:', error);
    }
  };

  const handleSend = async (nlId) => {
    if (!window.confirm('Inviare la newsletter a tutti i destinatari?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/marketing/newsletters/${nlId}/send`, {}, getAuthHeaders());
      alert(`Newsletter inviata a ${res.data.sent} destinatari!`);
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa newsletter?')) return;
    try {
      await axios.delete(`${API}/marketing/newsletters/${id}`, getAuthHeaders());
      onRefresh();
    } catch (error) {
      console.error('Errore eliminazione:', error);
    }
  };

  const emailCount = customers.filter(c => c.email).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-outfit flex items-center gap-2">
              <Mail className="w-5 h-5" />Newsletter
            </CardTitle>
            <CardDescription>Crea e invia newsletter ai tuoi clienti ({emailCount} con email)</CardDescription>
          </div>
          <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openEditor()} data-testid="create-newsletter-btn">
                <Plus className="w-4 h-4 mr-2" />Crea Newsletter
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-outfit">{editingNl ? 'Modifica Newsletter' : 'Nuova Newsletter'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-5 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Newsletter *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Newsletter Marzo" required data-testid="newsletter-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Oggetto Email *</Label>
                    <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="es. Le novità del mese" required data-testid="newsletter-subject-input" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Mittente</Label>
                    <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="es. Salone Bellezza" data-testid="newsletter-sender-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Rispondi-a</Label>
                    <Input type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} placeholder="es. info@salone.it" data-testid="newsletter-reply-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Destinatari</Label>
                  <Select value={form.recipient_filter} onValueChange={(val) => setForm({ ...form, recipient_filter: val })}>
                    <SelectTrigger data-testid="newsletter-recipients-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i clienti con email ({emailCount})</SelectItem>
                      <SelectItem value="recent">Clienti recenti (ultimi 30gg)</SelectItem>
                      <SelectItem value="active">Clienti attivi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Email Builder */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Contenuto Email</Label>
                  <EmailBuilder
                    blocks={form.blocks}
                    onChange={handleBuilderChange}
                    branding={branding}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsEditorOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="save-newsletter-btn">{editingNl ? 'Aggiorna' : 'Salva'} Bozza</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {newsletters.length > 0 ? (
            <div className="space-y-3">
              {newsletters.map((nl) => (
                <div key={nl.id} className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-start gap-x-3 gap-y-2 p-3 sm:p-4 rounded-lg border" data-testid={`newsletter-item-${nl.id}`}>
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="min-w-0">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold break-words">{nl.name}</p>
                        <Badge variant={nl.status === 'sent' ? 'default' : 'secondary'} className="text-[10px] gap-1">
                          {nl.status === 'sent' ? <><CheckCircle2 className="w-3 h-3" />Inviata</> : <><Clock className="w-3 h-3" />Bozza</>}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground break-words">{nl.subject}</p>
                      {nl.stats?.sent > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <Users className="w-3 h-3 inline mr-1" />{nl.stats.sent} inviate
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex items-center justify-end gap-2 border-t sm:border-t-0 pt-2 sm:pt-0">
                    {nl.status === 'draft' && (
                      <Button variant="outline" size="sm" className="min-h-[40px]" onClick={() => handleSend(nl.id)} disabled={sending} data-testid={`send-nl-${nl.id}`}>
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Invia
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="min-h-[40px] sm:w-9 sm:h-9 sm:p-0" onClick={() => openEditor(nl)} data-testid={`edit-nl-${nl.id}`}>
                      <Edit className="w-4 h-4 mr-2 sm:mr-0" /><span className="sm:hidden">Modifica</span>
                    </Button>
                    <Button variant="outline" size="sm" className="min-h-[40px] sm:w-9 sm:h-9 sm:p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(nl.id)} data-testid={`delete-nl-${nl.id}`}>
                      <Trash2 className="w-4 h-4 mr-2 sm:mr-0" /><span className="sm:hidden">Elimina</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="font-semibold text-lg mb-1">Nessuna newsletter</h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto">
                Crea la tua prima newsletter per comunicare promozioni, novità e aggiornamenti ai tuoi clienti.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterTab;
